import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import { createPopper } from '@popperjs/core';
import { match, removeAccents } from '@/libs/strings';

function estaVacio(valor) {
  if (valor === 0) return false;
  if (Array.isArray(valor) && valor.length === 0) return true;
  return !valor;
}

function incluyeTexto(texto, consulta) {
  return match(texto, consulta.trim());
}

export function tieneEstructuraAgrupada(opciones, valoresGrupo) {
  if (!valoresGrupo || !opciones?.length) return false;
  return opciones.some((opcion) => Array.isArray(opcion[valoresGrupo]));
}

function aplanarGrupos(opciones, valoresGrupo, etiquetaGrupo) {
  return opciones.reduce((prev, curr) => {
    const valores = curr[valoresGrupo];
    if (Array.isArray(valores) && valores.length) {
      prev.push({
        $groupLabel: curr[etiquetaGrupo],
        $isLabel: true,
      });
      return prev.concat(valores);
    }
    return prev;
  }, []);
}

function quitarEtiquetasGrupo(opciones) {
  return opciones.filter((opcion) => !opcion.$isLabel);
}

function filtrarOpciones(opciones, consulta, campoEtiqueta, etiquetaPersonalizada) {
  return opciones.filter((opcion) => {
    if (opcion.$isLabel || opcion.$isDisabled) return true;
    const etiqueta = etiquetaPersonalizada(opcion, campoEtiqueta);
    return incluyeTexto(etiqueta, consulta);
  });
}

function filtrarGrupos(
  opciones,
  consulta,
  valoresGrupo,
  etiquetaGrupo,
  campoEtiqueta,
  etiquetaPersonalizada,
) {
  return opciones.filter((grupo) => {
    const valores = grupo[valoresGrupo];
    if (!Array.isArray(valores)) return false;
    const etiquetaGrupoTexto = grupo[etiquetaGrupo];
    if (incluyeTexto(etiquetaGrupoTexto, consulta)) return true;
    return valores.some((opcion) => {
      const etiqueta = etiquetaPersonalizada(opcion, campoEtiqueta);
      return incluyeTexto(etiqueta, consulta);
    });
  }).map((grupo) => {
    const valores = grupo[valoresGrupo];
    if (!Array.isArray(valores)) return grupo;
    if (incluyeTexto(grupo[etiquetaGrupo], consulta)) return grupo;
    return {
      ...grupo,
      [valoresGrupo]: valores.filter((opcion) => {
        const etiqueta = etiquetaPersonalizada(opcion, campoEtiqueta);
        return incluyeTexto(etiqueta, consulta);
      }),
    };
  });
}

export default function useSelectCifra(props, emit, modelInterno) {
  const abierto = ref(false);
  const busqueda = ref('');
  const indiceResaltado = ref(-1);
  const contenedorRef = ref(null);
  const triggerRef = ref(null);
  const menuRef = ref(null);
  const inputBusquedaRef = ref(null);
  const popperInstance = ref(null);

  const etiquetaPorDefecto = (opcion, campoEtiqueta) => {
    if (estaVacio(opcion)) return '';
    return campoEtiqueta ? opcion[campoEtiqueta] : opcion;
  };

  const obtenerEtiquetaPersonalizada = computed(() => (
    props.etiquetaPersonalizada || etiquetaPorDefecto
  ));

  const valorInterno = computed(() => {
    const valor = modelInterno.value;
    if (valor || valor === 0) {
      return Array.isArray(valor) ? valor : [valor];
    }
    return [];
  });

  const clavesSeleccionadas = computed(() => {
    if (props.campoId) {
      return valorInterno.value.map((elemento) => elemento[props.campoId]);
    }
    return valorInterno.value;
  });

  function obtenerEtiquetaOpcion(opcion) {
    if (estaVacio(opcion)) return '';
    if (opcion.isTag) return opcion.label;
    if (opcion.$isLabel) return opcion.$groupLabel;
    const etiqueta = obtenerEtiquetaPersonalizada.value(opcion, props.campoEtiqueta);
    if (estaVacio(etiqueta)) return '';
    return etiqueta;
  }

  function obtenerClaveOpcion(opcion) {
    if (props.campoId && opcion && typeof opcion === 'object') {
      return opcion[props.campoId];
    }
    return opcion;
  }

  function estaSeleccionada(opcion) {
    const clave = obtenerClaveOpcion(opcion);
    return clavesSeleccionadas.value.indexOf(clave) > -1;
  }

  function opcionDeshabilitada(opcion) {
    return !!opcion.$isDisabled;
  }

  function opcionesAplanadas(opciones) {
    if (
      props.valoresGrupo
      && props.etiquetaGrupo
      && tieneEstructuraAgrupada(opciones, props.valoresGrupo)
    ) {
      return aplanarGrupos(opciones, props.valoresGrupo, props.etiquetaGrupo);
    }
    return opciones;
  }

  function existeOpcion(consulta) {
    const opcionesBase = props.opciones || [];
    const opciones = tieneEstructuraAgrupada(opcionesBase, props.valoresGrupo)
      ? quitarEtiquetasGrupo(opcionesAplanadas(opcionesBase))
      : opcionesBase;
    const consultaNormalizada = removeAccents(consulta).toLowerCase();
    return opciones.some((opcion) => (
      removeAccents(obtenerEtiquetaOpcion(opcion).toString()).toLowerCase() === consultaNormalizada
    ));
  }

  function destruirPopper() {
    if (popperInstance.value) {
      popperInstance.value.destroy();
      popperInstance.value = null;
    }
  }

  function cerrar() {
    abierto.value = false;
    indiceResaltado.value = -1;
    busqueda.value = '';
    destruirPopper();
  }

  function actualizarAnchoMenu() {
    if (triggerRef.value && menuRef.value) {
      menuRef.value.style.minWidth = `${triggerRef.value.offsetWidth}px`;
    }
  }

  const opcionesFiltradas = computed(() => {
    const consulta = busqueda.value || '';
    const consultaNormalizada = consulta.trim();
    let opciones = (props.opciones || []).concat();
    const agrupadas = props.valoresGrupo
      && props.etiquetaGrupo
      && tieneEstructuraAgrupada(opciones, props.valoresGrupo);

    if (props.busquedaInterna && props.buscable) {
      if (agrupadas) {
        opciones = filtrarGrupos(
          opciones,
          consultaNormalizada,
          props.valoresGrupo,
          props.etiquetaGrupo,
          props.campoEtiqueta,
          obtenerEtiquetaPersonalizada.value,
        );
        opciones = aplanarGrupos(opciones, props.valoresGrupo, props.etiquetaGrupo);
      } else {
        opciones = filtrarOpciones(
          opciones,
          consultaNormalizada,
          props.campoEtiqueta,
          obtenerEtiquetaPersonalizada.value,
        );
      }
    } else if (agrupadas) {
      opciones = aplanarGrupos(opciones, props.valoresGrupo, props.etiquetaGrupo);
    }

    const puedeCrearEtiqueta = props.permiteEtiquetas
      && consultaNormalizada.length
      && !existeOpcion(consultaNormalizada);
    if (puedeCrearEtiqueta) {
      opciones.unshift({ isTag: true, label: consulta });
    }

    if (props.ocultarSeleccionados && props.multiple) {
      opciones = opciones.filter((opcion) => (
        opcion.$isLabel || opcion.$isDisabled || opcion.isTag || !estaSeleccionada(opcion)
      ));
    }

    return opciones;
  });

  const opcionesSeleccionables = computed(() => (
    opcionesFiltradas.value.filter((opcion) => !opcion.$isLabel && !opcion.$isDisabled)
  ));

  function resaltarPrimeraOpcion() {
    indiceResaltado.value = opcionesSeleccionables.value.length > 0 ? 0 : -1;
  }

  function abrir() {
    if (props.deshabilitado) return;
    abierto.value = true;
    nextTick(() => {
      resaltarPrimeraOpcion();
      actualizarAnchoMenu();
      if (triggerRef.value && menuRef.value) {
        popperInstance.value = createPopper(triggerRef.value, menuRef.value, {
          placement: 'bottom-start',
          strategy: 'fixed',
          modifiers: [
            { name: 'offset', options: { offset: [0, 2] } },
            { name: 'preventOverflow', options: { padding: 4 } },
          ],
        });
      }
      if (props.buscable && inputBusquedaRef.value) {
        inputBusquedaRef.value.focus();
      }
    });
  }

  function emitirValor(nuevoValor) {
    modelInterno.value = nuevoValor;
  }

  function grupoCompletoSeleccionado(grupo) {
    const valores = grupo[props.valoresGrupo] || [];
    return valores.length > 0 && valores.every((opcion) => estaSeleccionada(opcion));
  }

  function seleccionarGrupoCompleto(etiquetaGrupo) {
    const grupo = (props.opciones || []).find(
      (opcion) => opcion[props.etiquetaGrupo] === etiquetaGrupo.$groupLabel,
    );
    if (!grupo) return;

    const valoresGrupo = grupo[props.valoresGrupo] || [];
    if (grupoCompletoSeleccionado(grupo)) {
      const clavesGrupo = props.campoId
        ? valoresGrupo.map((val) => val[props.campoId])
        : valoresGrupo;
      const nuevoValor = valorInterno.value.filter(
        (opcion) => clavesGrupo.indexOf(obtenerClaveOpcion(opcion)) === -1,
      );
      emitirValor(nuevoValor);
    } else {
      const nuevasOpciones = valoresGrupo.filter((opcion) => !estaSeleccionada(opcion));
      emitirValor(valorInterno.value.concat(nuevasOpciones));
    }
  }

  function quitarOpcion(opcion) {
    const clave = obtenerClaveOpcion(opcion);
    if (props.multiple) {
      const nuevoValor = valorInterno.value.filter(
        (elemento) => obtenerClaveOpcion(elemento) !== clave,
      );
      emitirValor(nuevoValor);
    } else {
      emitirValor(null);
    }
  }

  function seleccionarOpcion(opcion) {
    if (opcion.$isLabel && props.seleccionarGrupo) {
      seleccionarGrupoCompleto(opcion);
      return;
    }
    if (props.deshabilitado || opcionDeshabilitada(opcion) || opcion.$isLabel) return;

    if (opcion.isTag) {
      emit('nueva-etiqueta', opcion.label);
      busqueda.value = '';
      if (!props.multiple) cerrar();
      return;
    }

    if (estaSeleccionada(opcion)) {
      if (props.permitirVacio) {
        quitarOpcion(opcion);
      }
      return;
    }

    if (props.multiple) {
      emitirValor(valorInterno.value.concat([opcion]));
    } else {
      emitirValor(opcion);
      busqueda.value = '';
      cerrar();
    }
  }

  function limpiarTodo() {
    emitirValor(props.multiple ? [] : null);
    busqueda.value = '';
  }

  const valoresVisibles = computed(() => {
    if (!props.multiple) return [];
    if (props.limite && valorInterno.value.length > props.limite) {
      return valorInterno.value.slice(0, props.limite);
    }
    return valorInterno.value;
  });

  const cantidadOculta = computed(() => {
    if (!props.limite || !props.multiple) return 0;
    return Math.max(0, valorInterno.value.length - props.limite);
  });

  const etiquetaSeleccionada = computed(() => {
    if (props.multiple || valorInterno.value.length === 0) return '';
    return obtenerEtiquetaOpcion(valorInterno.value[0]);
  });

  const mostrarMarcador = computed(() => {
    if (props.buscable) return false;
    return valorInterno.value.length === 0 && !busqueda.value;
  });

  const marcadorInput = computed(() => {
    if (!props.multiple && valorInterno.value.length > 0 && abierto.value) {
      return etiquetaSeleccionada.value;
    }
    if (props.multiple && valorInterno.value.length > 0) return '';
    return props.marcador || '';
  });

  function toggle() {
    if (abierto.value) {
      cerrar();
    } else {
      abrir();
    }
  }

  function actualizarBusqueda(valor) {
    busqueda.value = valor;
    if (!abierto.value) abrir();
    else resaltarPrimeraOpcion();
  }

  function indiceResaltadoValido(direccion) {
    const total = opcionesSeleccionables.value.length;
    if (total === 0) return -1;
    if (indiceResaltado.value < 0) {
      return direccion > 0 ? 0 : total - 1;
    }
    let nuevo = indiceResaltado.value + direccion;
    if (nuevo < 0) nuevo = 0;
    if (nuevo >= total) nuevo = total - 1;
    return nuevo;
  }

  function resaltarOpcion(opcion) {
    if (opcion.$isLabel || opcion.$isDisabled) return;
    const indice = opcionesSeleccionables.value.indexOf(opcion);
    if (indice >= 0) indiceResaltado.value = indice;
  }

  function quitarResaltado() {
    resaltarPrimeraOpcion();
  }

  function resaltarAnterior() {
    const indice = indiceResaltadoValido(-1);
    if (indice >= 0) indiceResaltado.value = indice;
  }

  function resaltarSiguiente() {
    const indice = indiceResaltadoValido(1);
    if (indice >= 0) indiceResaltado.value = indice;
  }

  function resaltarSiguientePagina() {
    const indice = indiceResaltadoValido(10);
    if (indice >= 0) indiceResaltado.value = indice;
  }

  function resaltarAnteriorPagina() {
    const indice = indiceResaltadoValido(-10);
    if (indice >= 0) indiceResaltado.value = indice;
  }

  function resaltarPrimero() {
    if (opcionesSeleccionables.value.length > 0) indiceResaltado.value = 0;
  }

  function resaltarUltimo() {
    const total = opcionesSeleccionables.value.length;
    if (total > 0) indiceResaltado.value = total - 1;
  }

  watch(indiceResaltado, () => {
    nextTick(() => {
      if (!abierto.value || indiceResaltado.value < 0 || !menuRef.value) return;
      const resaltada = menuRef.value.querySelector('.select-cifra__opcion--resaltada');
      if (resaltada) {
        resaltada.scrollIntoView({ block: 'nearest' });
      }
    });
  });

  function seleccionarResaltada() {
    const opcion = opcionesSeleccionables.value[indiceResaltado.value];
    if (opcion) seleccionarOpcion(opcion);
  }

  function quitarUltimaSeleccion() {
    if (!props.multiple || busqueda.value) return;
    if (valorInterno.value.length > 0) {
      const ultima = valorInterno.value[valorInterno.value.length - 1];
      quitarOpcion(ultima);
    }
  }

  function onKeydownTrigger(event) {
    if (props.deshabilitado) return;
    if (props.buscable && abierto.value && event.target === inputBusquedaRef.value) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!abierto.value) abrir();
        else resaltarSiguiente();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (abierto.value) resaltarAnterior();
        break;
      case 'Enter':
        event.preventDefault();
        if (abierto.value) seleccionarResaltada();
        else abrir();
        break;
      case 'Escape':
        event.preventDefault();
        cerrar();
        break;
      case 'Backspace':
        if (props.multiple) quitarUltimaSeleccion();
        break;
      case 'Tab':
        if (abierto.value && indiceResaltado.value >= 0) {
          seleccionarResaltada();
        }
        break;
      case 'PageDown':
        event.preventDefault();
        if (!abierto.value) abrir();
        else resaltarSiguientePagina();
        break;
      case 'PageUp':
        event.preventDefault();
        if (abierto.value) resaltarAnteriorPagina();
        break;
      case 'Home':
        event.preventDefault();
        if (abierto.value) resaltarPrimero();
        break;
      case 'End':
        event.preventDefault();
        if (abierto.value) resaltarUltimo();
        break;
      default: {
        const esCaracter = event.key.length === 1
          && !event.metaKey && !event.ctrlKey && !event.altKey;
        if (props.buscable && esCaracter) {
          event.preventDefault();
          busqueda.value = event.key;
          if (!abierto.value) {
            abrir();
          } else {
            nextTick(() => inputBusquedaRef.value?.focus());
          }
        }
        break;
      }
    }
  }

  function onKeydownInput(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        resaltarSiguiente();
        break;
      case 'ArrowUp':
        event.preventDefault();
        resaltarAnterior();
        break;
      case 'Enter':
        event.preventDefault();
        seleccionarResaltada();
        break;
      case 'Escape':
        event.preventDefault();
        cerrar();
        break;
      case 'Backspace':
        if (props.multiple && !busqueda.value) quitarUltimaSeleccion();
        break;
      case 'Tab':
        if (abierto.value && indiceResaltado.value >= 0) {
          seleccionarResaltada();
        }
        break;
      case 'PageDown':
        event.preventDefault();
        resaltarSiguientePagina();
        break;
      case 'PageUp':
        event.preventDefault();
        resaltarAnteriorPagina();
        break;
      case 'Home':
        event.preventDefault();
        resaltarPrimero();
        break;
      case 'End':
        event.preventDefault();
        resaltarUltimo();
        break;
      default:
        break;
    }
  }

  function onClickOutside(event) {
    if (!abierto.value) return;
    if (contenedorRef.value?.contains(event.target)) return;
    cerrar();
  }

  function onScroll(event) {
    if (!abierto.value) return;
    if (contenedorRef.value?.contains(event.target)) return;
    cerrar();
  }

  function onResize() {
    if (abierto.value) {
      actualizarAnchoMenu();
      popperInstance.value?.update();
    }
  }

  function esResaltada(opcion) {
    if (!abierto.value || indiceResaltado.value < 0) return false;
    const indice = opcionesSeleccionables.value.indexOf(opcion);
    return indice === indiceResaltado.value;
  }

  function estaGrupoSeleccionado(opcionEtiqueta) {
    const grupo = (props.opciones || []).find(
      (opcion) => opcion[props.etiquetaGrupo] === opcionEtiqueta.$groupLabel,
    );
    if (!grupo) return false;
    return grupoCompletoSeleccionado(grupo);
  }

  function obtenerValorInputOculto(opcion) {
    if (props.campoId && opcion && typeof opcion === 'object') {
      return opcion[props.campoId];
    }
    return opcion;
  }

  watch(busqueda, (valor) => {
    emit('cambio-busqueda', valor);
  });

  watch(() => props.opciones, () => {
    if (abierto.value) {
      resaltarPrimeraOpcion();
    }
  });

  onMounted(() => {
    document.addEventListener('click', onClickOutside, true);
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onResize);
    if (props.autofoco) {
      nextTick(() => triggerRef.value?.focus());
    }
  });

  onUnmounted(() => {
    document.removeEventListener('click', onClickOutside, true);
    window.removeEventListener('scroll', onScroll, { capture: true, passive: true });
    window.removeEventListener('resize', onResize);
    destruirPopper();
  });

  return {
    abierto,
    busqueda,
    contenedorRef,
    triggerRef,
    menuRef,
    inputBusquedaRef,
    opcionesFiltradas,
    valoresVisibles,
    cantidadOculta,
    etiquetaSeleccionada,
    mostrarMarcador,
    marcadorInput,
    obtenerEtiquetaOpcion,
    estaSeleccionada,
    opcionDeshabilitada,
    esResaltada,
    resaltarOpcion,
    quitarResaltado,
    seleccionarOpcion,
    quitarOpcion,
    limpiarTodo,
    toggle,
    cerrar,
    abrir,
    actualizarBusqueda,
    onKeydownTrigger,
    onKeydownInput,
    obtenerValorInputOculto,
    valorInterno,
    estaGrupoSeleccionado,
  };
}
